import { CheckBadgeIcon } from "@heroicons/react/24/outline";
import { CalendarDate, genEntityId, Task, TaskParentId } from "@sp-crm/core";
import { ListSection } from "components/ui/list-section";
import { Panel } from "components/ui/panel/panel";
import { PanelType } from "components/ui/panel/panel-type";
import { TaskOwnerViewableKind } from "constants/tasks";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    allTaskTypesSentinel,
    FilterableTaskTypes,
    taskDashboardSlice,
    TasksAll,
    unsetTaskTypeSentinel,
} from "store/slices/tasks-dashboard";
import { ApplicationState } from "store/state";
import {
    GetUsersForRegionQuery,
    TaskBasicsFragment,
    UpdateTaskPayload,
    useCreateTaskMutation,
} from "../../../generated/graphql";
import { useTaskNouns } from "../../../hooks/use-task-nouns";
import { handleEvent } from "../../../util/user-events";
import { TaskFormV2 } from "./task-form-v2";
import { TaskLineItem } from "./task-line-item";
import { Taskv2 } from "./types";

interface TaskListv2Props {
    refetch: () => Promise<void>;
    tasks: Taskv2[];
    taskTypeFilter: FilterableTaskTypes;
    entityFilter: TaskOwnerViewableKind;
    users: GetUsersForRegionQuery["getUsersForRegion"];
    queryString?: string;
    modifyLocalTask: (task: Taskv2) => void;
}

interface AddPanelState {
    isOpen: boolean;
    sourceTask: Taskv2 | null;
}

export const TaskListv2: React.FunctionComponent<TaskListv2Props> = props => {
    const { refetch, taskTypeFilter, entityFilter, queryString, modifyLocalTask, users } =
        props;
    const nouns = useTaskNouns();
    const [selectedTask, setSelectedTask] = React.useState<Taskv2 | null>(null);
    const [addPanelState, setAddPanelState] = React.useState<AddPanelState>({
        isOpen: false,
        sourceTask: null,
    });

    const createTask = useCreateTaskMutation();

    let tasks = props.tasks;

    const sectionVisibility = useSelector(
        (state: ApplicationState) => state.taskDashboard.sectionVisibility,
    );
    const toggleSection = taskDashboardSlice.actions.toggleSectionVisibility;
    const dispatch = useDispatch();

    const handleTaskSelected = React.useCallback(
        (task: Taskv2) => {
            setSelectedTask(task);
        },
        [setSelectedTask],
    );

    const handleTaskUnselected = React.useCallback(() => {
        setSelectedTask(null);
    }, [setSelectedTask]);

    const handleAddTask = React.useCallback(
        (source: Taskv2) => {
            setAddPanelState({ isOpen: true, sourceTask: source });
        },
        [setAddPanelState],
    );

    const hideAddPanel = React.useCallback(() => {
        setAddPanelState({ isOpen: false, sourceTask: null });
    }, [setAddPanelState]);

    const addPanelInitialTask: Taskv2 = React.useMemo(() => {
        return {
            entity: addPanelState.sourceTask
                ? { ...addPanelState.sourceTask.entity }
                : null,
            task: {
                __typename: "Task" as const,
                done: false,
                id: genEntityId(),
                parentId: addPanelState?.sourceTask?.task?.parentId || null,
                status: "new",
                completedAt: null as TaskBasicsFragment["completedAt"],
                dueDate: CalendarDate.today.toString(),
                dueDateTime: null as TaskBasicsFragment["dueDateTime"],
                ownerId: addPanelState?.sourceTask?.task?.ownerId || null,
                taskTypeId: null as TaskBasicsFragment["taskTypeId"],
                taskType: null as TaskBasicsFragment["taskType"],
                text: "",
                notes: null as TaskBasicsFragment["notes"],
                recurrencePattern: null as TaskBasicsFragment["recurrencePattern"],
                recurrenceInterval: null as TaskBasicsFragment["recurrenceInterval"],
                recurrenceDaysOfWeek: null as TaskBasicsFragment["recurrenceDaysOfWeek"],
                recurrenceDayOfMonth: null as TaskBasicsFragment["recurrenceDayOfMonth"],
            },
        };
    }, [addPanelState.sourceTask]);

    const handleTaskCreated = React.useCallback(
        async (parentId: TaskParentId, params: UpdateTaskPayload) => {
            await createTask.mutateAsync({
                parentId,
                params,
            });
            handleEvent("task-create");
            await refetch();
        },
        [createTask, refetch],
    );

    if (!tasks || tasks.length === 0) {
        return (
            <div className="flex items-center space-x-2">
                <CheckBadgeIcon className="w-8 h-8 text-green-400" />
                <div>{`No ${nouns.plural}!`}</div>
            </div>
        );
    }

    if (entityFilter && entityFilter !== TasksAll) {
        tasks = tasks.filter(t => t.entity.kind === entityFilter);
    }
    if (taskTypeFilter === unsetTaskTypeSentinel) {
        tasks = tasks.filter(t => !t.task.taskType);
    } else if (taskTypeFilter && taskTypeFilter !== allTaskTypesSentinel) {
        tasks = tasks.filter(t => taskTypeFilter === t.task.taskType?.id);
    }
    if (queryString && queryString !== "") {
        const searchText = queryString.toLowerCase();
        tasks = tasks.filter(t => {
            const targetText = `${t.entity.displayName} ${t.task.text}`.toLowerCase();
            return targetText.includes(searchText);
        });
    }

    if (tasks.length === 0) {
        return (
            <div className="flex items-center space-x-2">
                <div>
                    <em>{`No ${nouns.plural} match search.`}</em>
                </div>
            </div>
        );
    }

    const mostRecentMidnight = new Date();
    mostRecentMidnight.setHours(0, 0, 0, 0);

    const midnightTomorrow = new Date();
    midnightTomorrow.setDate(midnightTomorrow.getDate() + 1);
    midnightTomorrow.setHours(23, 59, 59, 999);

    const midnightTonight = new Date();
    midnightTonight.setHours(23, 59, 59, 999);

    const overdueTasks: Taskv2[] = [];
    const dueToday: Taskv2[] = [];
    const dueTomorrow: Taskv2[] = [];
    const dueLater: Taskv2[] = [];
    const noDueDate: Taskv2[] = [];

    tasks.forEach(task => {
        const effectiveDate = Task.load(task.task).when();
        if (effectiveDate) {
            if (effectiveDate < mostRecentMidnight) {
                overdueTasks.push(task);
            } else if (effectiveDate < midnightTonight) {
                dueToday.push(task);
            } else if (effectiveDate < midnightTomorrow) {
                dueTomorrow.push(task);
            } else {
                dueLater.push(task);
            }
        } else {
            noDueDate.push(task);
        }
    });

    const renderList = (sectionTasks: Taskv2[]) => (
        <ListTaskv2
            refetch={refetch}
            tasks={sectionTasks}
            selectedTask={selectedTask}
            onTaskSelected={handleTaskSelected}
            onTaskUnselected={handleTaskUnselected}
            onAddTask={handleAddTask}
            modifyLocalTask={modifyLocalTask}
            users={users}
        />
    );

    return (
        <div className="space-y-3 sm:space-y-4">
            {overdueTasks.length > 0 ? (
                <ListSection
                    label="Overdue"
                    tone="alert"
                    count={overdueTasks.length}
                    disclosure={{
                        kind: "collapsible",
                        open: sectionVisibility.overdue,
                        onToggle: () => dispatch(toggleSection("overdue")),
                    }}>
                    {renderList(overdueTasks)}
                </ListSection>
            ) : null}
            {dueToday.length > 0 ? (
                <ListSection
                    label="Due Today"
                    tone="accent"
                    count={dueToday.length}
                    disclosure={{
                        kind: "collapsible",
                        open: sectionVisibility.today,
                        onToggle: () => dispatch(toggleSection("today")),
                    }}>
                    {renderList(dueToday)}
                </ListSection>
            ) : null}
            {dueTomorrow.length > 0 ? (
                <ListSection
                    label="Due Tomorrow"
                    tone="accent"
                    count={dueTomorrow.length}
                    disclosure={{
                        kind: "collapsible",
                        open: sectionVisibility.tomorrow,
                        onToggle: () => dispatch(toggleSection("tomorrow")),
                    }}>
                    {renderList(dueTomorrow)}
                </ListSection>
            ) : null}
            {dueLater.length > 0 ? (
                <ListSection
                    label="Later"
                    tone="accent"
                    count={dueLater.length}
                    disclosure={{
                        kind: "collapsible",
                        open: sectionVisibility.future,
                        onToggle: () => dispatch(toggleSection("future")),
                    }}>
                    {renderList(dueLater)}
                </ListSection>
            ) : null}
            {noDueDate.length > 0 ? (
                <ListSection
                    label="No due date"
                    tone="accent"
                    count={noDueDate.length}
                    disclosure={{
                        kind: "collapsible",
                        open: sectionVisibility.none,
                        onToggle: () => dispatch(toggleSection("none")),
                    }}>
                    {renderList(noDueDate)}
                </ListSection>
            ) : null}
            <Panel
                type={PanelType.large}
                headerText={`Add new ${nouns.singular}`}
                isOpen={addPanelState.isOpen}
                onDismiss={hideAddPanel}>
                <TaskFormV2
                    initial={addPanelInitialTask}
                    mode="create"
                    onClose={hideAddPanel}
                    saveTask={handleTaskCreated}
                />
            </Panel>
        </div>
    );
};

interface ListTaskv2Props {
    tasks: Taskv2[];
    refetch: () => Promise<void>;
    selectedTask: Taskv2 | null;
    onTaskSelected: (task: Taskv2) => void;
    onTaskUnselected: (task: Taskv2) => void;
    onAddTask: (task: Taskv2) => void;
    modifyLocalTask: (task: Taskv2) => void;
    users: GetUsersForRegionQuery["getUsersForRegion"];
}

export const ListTaskv2: React.FC<ListTaskv2Props> = props => {
    const {
        refetch,
        tasks,
        onTaskSelected,
        onTaskUnselected,
        onAddTask,
        modifyLocalTask,
        users,
    } = props;
    return (
        <ul className="divide-y divide-gray-100 px-2 sm:px-4">
            {tasks.map(task => (
                <li key={task.task.id}>
                    <TaskLineItem
                        modifyLocalTask={modifyLocalTask}
                        task={task}
                        refetch={refetch}
                        isSelected={task.task.id === props.selectedTask?.task.id}
                        onSelected={onTaskSelected}
                        onUnselected={onTaskUnselected}
                        onAddTask={onAddTask}
                        users={users}
                    />
                </li>
            ))}
        </ul>
    );
};
